import { useCallback, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, Paper } from '@mui/material';
import { useSearchParams } from 'react-router';

import TabPanel from '@/components/TabPanel';
import Tabs from '@/components/Tabs';
import { PageInformationContext } from '@/context/PageInformationContext';
import FactsheetWindow from '@/features/Factsheet/components/FactsheetView/FactsheetWindow';
import { FACTSHEET_TAB_PARAM_NAME } from '@/features/Factsheet/constants';
import updateTabNames from '@/features/Factsheet/utils/updateTabNames';
import { addStringIfNotExists } from '@/utils/addStringIfNotExists';
import filterOutElementAtIndex from '@/utils/filterOutElementAtIndex';
import splitCommaSeparatedKeys from '@/utils/splitCommaSeparatedKeys';

const paperSx = {
  width: 1024,
  maxWidth: '100%',
  mx: 8,
  my: 5,
  display: 'flex',
  flexDirection: 'column'
};
const headerSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 1.5,
  px: 3
};
const closeBtnSx = { width: 24, height: 24 };
const logoLinkSx = { display: 'flex' };
const tabPanelSx = {
  display: 'flex',
  flexGrow: 1,
  flex: 1,
  height: '100%',
  width: '100%'
};

interface FactsheetViewState {
  sourceKeys: string;
  activeTab: number;
  openedFactsheets: string[];
  tabNames: Map<string, string>;
}

const createFactsheetViewState = (factsheetKeys: string): FactsheetViewState => {
  const codes = splitCommaSeparatedKeys(factsheetKeys);
  return {
    sourceKeys: factsheetKeys,
    openedFactsheets: codes,
    activeTab: Math.max(codes.length - 1, 0),
    tabNames: new Map(codes.map(code => [code, code]))
  };
};

const FactsheetView = ({
  factsheetKeys,
  onDialogClose,
  setAdditionalTabsParam
}: {
  factsheetKeys: string;
  onDialogClose?: () => void;
  setAdditionalTabsParam?: (tabs: string[]) => void;
}) => {
  const [factsheetState, setFactsheetState] = useState(() =>
    createFactsheetViewState(factsheetKeys)
  );
  const currentFactsheetState = useMemo(() => {
    if (factsheetState.sourceKeys === factsheetKeys) return factsheetState;
    return createFactsheetViewState(factsheetKeys);
  }, [factsheetKeys, factsheetState]);
  const { activeTab, openedFactsheets, tabNames } = currentFactsheetState;
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const deleteActiveTabParam = useCallback(() => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.delete(FACTSHEET_TAB_PARAM_NAME);
    setUrlSearchParams(newSearchParams);
  }, [setUrlSearchParams, urlSearchParams]);

  const handleChangeTab = useCallback(
    (value: number) => {
      deleteActiveTabParam();
      setFactsheetState({ ...currentFactsheetState, activeTab: value });
    },
    [currentFactsheetState, deleteActiveTabParam]
  );

  const onClose = useCallback(
    (indexToClose: number) => {
      const updated = filterOutElementAtIndex(openedFactsheets, indexToClose);

      if (setAdditionalTabsParam) {
        setAdditionalTabsParam(updated.slice(1));
      }

      const nextActiveTab = indexToClose <= activeTab ? Math.max(activeTab - 1, 0) : activeTab;
      setFactsheetState({
        ...currentFactsheetState,
        openedFactsheets: updated,
        activeTab: nextActiveTab
      });
      deleteActiveTabParam();
    },

    [
      activeTab,
      currentFactsheetState,
      deleteActiveTabParam,
      openedFactsheets,
      setAdditionalTabsParam
    ]
  );

  const handleUpdateTabName = useCallback(
    (code: string, newName: string) => {
      setFactsheetState(prev => {
        const currentState =
          prev.sourceKeys === factsheetKeys ? prev : createFactsheetViewState(factsheetKeys);
        return {
          ...currentState,
          tabNames: updateTabNames(currentState.tabNames, code, newName)
        };
      });
    },
    [factsheetKeys]
  );

  const handleOpenNewTab = useCallback(
    (code: string) => {
      const currentIndex = openedFactsheets.indexOf(code);

      if (currentIndex !== -1) {
        setFactsheetState({ ...currentFactsheetState, activeTab: currentIndex });
        return;
      }

      const updated = addStringIfNotExists(openedFactsheets, code);

      if (setAdditionalTabsParam && updated.length > 1) {
        setAdditionalTabsParam(updated.slice(1));
      }

      setFactsheetState({
        ...currentFactsheetState,
        openedFactsheets: updated,
        activeTab: updated.length - 1,
        tabNames: updateTabNames(tabNames, code, code)
      });
    },
    [currentFactsheetState, openedFactsheets, setAdditionalTabsParam, tabNames]
  );
  const pageInformation = useMemo(() => {
    return {
      mainPageTitle: 'factsheets',
      subPageTitle: openedFactsheets[activeTab] ?? ''
    };
  }, [openedFactsheets, activeTab]);

  return (
    <PageInformationContext value={pageInformation}>
      <Paper sx={paperSx}>
        <Box sx={headerSx}>
          <Tabs
            activeTab={activeTab}
            tabs={openedFactsheets.map((code, index) => ({
              name: tabNames.get(code) ?? code,
              closable: !!index,
              onClose
            }))}
            onChange={handleChangeTab}
          />
          {onDialogClose ? (
            <ButtonBase
              aria-label='close'
              sx={closeBtnSx}
              onClick={event => {
                event.stopPropagation();
                onDialogClose();
              }}
            >
              <CloseIcon />
            </ButtonBase>
          ) : (
            <Box
              component='a'
              href='/'
              sx={logoLinkSx}
            >
              <img
                alt='logoGx'
                src='/gx-logo-white.svg'
                style={{
                  width: 130,
                  height: 40
                }}
              />
            </Box>
          )}
        </Box>

        {openedFactsheets.map((code, index) => (
          <TabPanel
            key={code}
            containerSX={{
              flexGrow: 1,
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              ...(index === activeTab ? { display: 'flex' } : {})
            }}
            index={index}
            sx={tabPanelSx}
            value={activeTab}
          >
            <FactsheetWindow
              factsheetKey={code}
              openNewTab={handleOpenNewTab}
              updateTabName={handleUpdateTabName}
            />
          </TabPanel>
        ))}
      </Paper>
    </PageInformationContext>
  );
};
export default FactsheetView;
